import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Smartphone,
  Star,
  Trash2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ApiError, apiFetch } from '@/lib/api';

type ContactKind = 'PHONE' | 'MOBILE' | 'WHATSAPP' | 'EMAIL' | 'OTHER';

interface Contact {
  id: string;
  clientId: string;
  kind: ContactKind;
  label: string;
  value: string;
  contactName: string | null;
  isPrimary: boolean;
  notes: string | null;
}

const KIND_LABEL: Record<ContactKind, string> = {
  PHONE: 'Telefone fixo',
  MOBILE: 'Celular',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Email',
  OTHER: 'Outro',
};

const KIND_ICON: Record<ContactKind, typeof Phone> = {
  PHONE: Phone,
  MOBILE: Smartphone,
  WHATSAPP: MessageCircle,
  EMAIL: Mail,
  OTHER: Phone,
};

function formatPhone(s: string): string {
  const d = s.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return s;
}

export function ClientContactsTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Contact | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ['contacts', clientId],
    queryFn: () => apiFetch<Contact[]>(`/clients/${clientId}/contacts`),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/clients/${clientId}/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts', clientId] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Telefones, emails e WhatsApps por área (comercial, financeiro, suporte etc.). Marque um como
          principal.
        </p>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Novo contato
        </button>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {list.data && list.data.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-12 text-center">
          <Phone className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Nenhum contato cadastrado.</p>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {list.data.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              onEdit={() => setEditing(c)}
              onDelete={() => {
                if (confirm(`Excluir contato "${c.label}"?`)) remove.mutate(c.id);
              }}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <ContactFormDialog
          clientId={clientId}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['contacts', clientId] });
          }}
        />
      )}
      {editing && (
        <ContactFormDialog
          clientId={clientId}
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['contacts', clientId] });
          }}
        />
      )}
    </div>
  );
}

function ContactCard({
  contact,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const Icon = KIND_ICON[contact.kind];
  const formatted =
    contact.kind === 'EMAIL' || contact.kind === 'OTHER'
      ? contact.value
      : formatPhone(contact.value);
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="rounded-md bg-primary/10 p-2 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">{contact.label}</span>
              {contact.isPrimary && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                  <Star className="h-2.5 w-2.5 fill-current" /> Principal
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">{KIND_LABEL[contact.kind]}</div>
            <a
              href={
                contact.kind === 'EMAIL'
                  ? `mailto:${contact.value}`
                  : contact.kind === 'WHATSAPP'
                    ? `https://wa.me/55${contact.value.replace(/\D/g, '')}`
                    : `tel:${contact.value.replace(/\D/g, '')}`
              }
              target="_blank"
              rel="noreferrer"
              className="mt-1 block font-mono text-sm text-primary hover:underline truncate"
            >
              {formatted}
            </a>
            {contact.contactName && (
              <div className="text-xs text-muted-foreground mt-1">{contact.contactName}</div>
            )}
            {contact.notes && (
              <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
                {contact.notes}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onEdit}
            data-dica="Editar"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-dica="Excluir"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactFormDialog({
  clientId,
  contact,
  onClose,
  onSaved,
}: {
  clientId: string;
  contact?: Contact;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    kind: (contact?.kind ?? 'PHONE') as ContactKind,
    label: contact?.label ?? '',
    value: contact?.value ?? '',
    contactName: contact?.contactName ?? '',
    isPrimary: contact?.isPrimary ?? false,
    notes: contact?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        kind: form.kind,
        label: form.label,
        value: form.value,
        contactName: form.contactName || undefined,
        isPrimary: form.isPrimary,
        notes: form.notes || undefined,
      });
      return isEdit
        ? apiFetch(`/clients/${clientId}/contacts/${contact!.id}`, {
            method: 'PATCH',
            body,
          })
        : apiFetch(`/clients/${clientId}/contacts`, { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-medium">
            {isEdit ? `Editar — ${contact!.label}` : 'Novo contato'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo *">
              <select
                required
                className="input"
                value={form.kind}
                onChange={(e) => setForm((s) => ({ ...s, kind: e.target.value as ContactKind }))}
              >
                {(Object.keys(KIND_LABEL) as ContactKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Rótulo *">
              <input
                required
                className="input"
                placeholder="Comercial, Financeiro, Suporte…"
                value={form.label}
                onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
              />
            </Field>
          </div>
          <Field
            label={
              form.kind === 'EMAIL'
                ? 'Email *'
                : form.kind === 'OTHER'
                  ? 'Valor *'
                  : 'Telefone (com DDD) *'
            }
          >
            <input
              required
              type={form.kind === 'EMAIL' ? 'email' : 'text'}
              className="input font-mono"
              placeholder={
                form.kind === 'EMAIL'
                  ? 'cobranca@cliente.com.br'
                  : '11999998888'
              }
              value={form.value}
              onChange={(e) => setForm((s) => ({ ...s, value: e.target.value }))}
            />
          </Field>
          <Field label="Nome da pessoa (opcional)">
            <input
              className="input"
              placeholder="João da Silva"
              value={form.contactName}
              onChange={(e) => setForm((s) => ({ ...s, contactName: e.target.value }))}
            />
          </Field>
          <Field label="Notas">
            <textarea
              rows={2}
              className="input"
              value={form.notes}
              onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isPrimary}
              onChange={(e) => setForm((s) => ({ ...s, isPrimary: e.target.checked }))}
            />
            Marcar como contato principal
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}
